import type { MediaItem } from "./types";

// PostType "format" codes that represent video content. STORY is not here:
// a Story can be an image or a video, so its type comes from the URL.
const VIDEO_FORMATS = new Set(["REEL", "SHORT", "VIDEO"]);
// Video file extensions — a robust fallback when `format` is a postType id
// (e.g. "instagram_reel") rather than a REEL/SHORT/STORY format code.
const VIDEO_EXT = /\.(mp4|mov|webm|m4v)(\?|#|$)/i;

export function formatToMediaType(format?: string | null): "VIDEO" | "IMAGE" {
  return format && VIDEO_FORMATS.has(format.toUpperCase()) ? "VIDEO" : "IMAGE";
}

/** Infer the media type for a real, playable URL from its format and extension. */
function typeForUrl(url: string, format?: string | null): "VIDEO" | "IMAGE" {
  return formatToMediaType(format) === "VIDEO" || VIDEO_EXT.test(url) ? "VIDEO" : "IMAGE";
}

interface PieceLikeMedia {
  id: string;
  mediaUrls?: string[] | null;
  thumbnailUrl?: string | null;
  format?: string | null;
  title?: string | null;
}

/**
 * Map a piece/event-shaped media object to a single MediaItem (the first media),
 * or null when there's nothing to show. Used by cards that render one preview.
 */
export function mediaItemFromPiece(input: PieceLikeMedia): MediaItem | null {
  const playable = input.mediaUrls?.[0] ?? null;
  const url = playable ?? input.thumbnailUrl ?? null;
  if (!url) return null;
  // Only treat it as video when there's a real playable url; a poster-only
  // piece (mediaUrls empty, thumbnail set) shows as an image, never <video>.
  return {
    id: input.id,
    type: playable ? typeForUrl(playable, input.format) : "IMAGE",
    url,
    thumbnailUrl: input.thumbnailUrl ?? null,
    filename: input.title ?? undefined,
  };
}

/**
 * Expand all of a piece's media URLs into MediaItems for a lightbox carousel.
 * The poster (thumbnailUrl) is attached to the first item only.
 */
export function mediaItemsFromPiece(input: PieceLikeMedia): MediaItem[] {
  const urls = input.mediaUrls ?? [];
  if (urls.length === 0) {
    const single = mediaItemFromPiece(input);
    return single ? [single] : [];
  }
  return urls.map((url, i) => ({
    id: `${input.id}-${i}`,
    type: typeForUrl(url, input.format),
    url,
    thumbnailUrl: i === 0 ? input.thumbnailUrl ?? null : null,
    filename: input.title ?? undefined,
  }));
}
